import { ChevronDown, Loader2, Plus } from 'lucide-react';
import React, { useEffect, useId, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { useTokenPairStore } from '@/features/tokenPairs';
import { useSpeedBalance } from '@/features/wallet';
import { cn } from '@/lib/utils';

import { usePositionForCoin } from '../hooks/useLivePosition';
import { useOpenTradeForCoin } from '../hooks/useOpenTradeForCoin';
import { useUpdateMargin } from '../hooks/useUpdateMargin';
import { selectIsRealMode, useTradingStore } from '../store';

interface PositionMarginEditorProps {
    /** Mobile host — tighter spacing, same controls. */
    compact?: boolean;
}

/** Smallest top-up worth an agent-signed action. */
const MIN_ADD_USD = 1;
const MAX_ADD_USD = 1_000_000;

function fmtUsd(value: number | null): string {
    if (value === null || !Number.isFinite(value)) return '—';
    return `$${value.toFixed(2)}`;
}

/** Floor to cents so "Max" never asks for a hair more than is there. */
function floorCents(value: number): number {
    return Math.floor(value * 100) / 100;
}

/**
 * Post-open margin editor for the live isolated position: adds USDC from the
 * account's free HyperCore margin into the position, which moves its
 * liquidation price away from mark. Add-only — the backend and the signer
 * cannot encode a removal.
 *
 * Real mode only, and only while an Open trade exists for the charted coin.
 * The "available to add" figure is the balance's HyperCore-withdrawable
 * bucket, which is the same free margin the backend checks; the backend is
 * still the authority and answers an oversized request with the amount it can
 * actually add. On success the editor collapses and the position query is
 * refetched so the margin/liquidation readout comes from Hyperliquid, never
 * from the draft.
 */
const PositionMarginEditor: React.FC<PositionMarginEditorProps> = ({
    compact,
}) => {
    const isRealMode = useTradingStore(selectIsRealMode);
    const selectedCoin = useTokenPairStore((s) => s.selectedCoin);
    const { mutate, isPending } = useUpdateMargin();

    // same resolution as the TP/SL editor: the rail follows the chart.
    const coin = selectedCoin;
    const trade = useOpenTradeForCoin(coin);
    const enabled = isRealMode && !!trade && !!coin;
    // cache hit for the polled coin; the reader is here so the readout does not
    // depend on which host mounted us.
    const position = usePositionForCoin(coin, enabled);
    const balance = useSpeedBalance({ enabled });

    const available =
        balance.hyperCoreWithdrawable !== null
            ? floorCents(balance.hyperCoreWithdrawable)
            : null;
    const maxAmount =
        available === null ? '' : String(Math.min(available, MAX_ADD_USD));
    // unknown (no live read yet) is allowed through: the backend enforces it.
    const isIsolated = position.marginType !== 'cross';

    const [expanded, setExpanded] = useState(false);
    const [draft, setDraft] = useState('');
    const fieldId = useId();

    // A collapsed editor drops whatever was typed; once it's open the user owns
    // the field and a balance refetch must not overwrite it.
    useEffect(() => {
        if (!expanded) setDraft('');
    }, [expanded]);

    useEffect(() => {
        setExpanded(false);
        setDraft('');
    }, [coin, trade?.id, isRealMode]);

    const amount = useMemo(() => {
        const text = draft.trim();
        if (text === '') return null;
        const value = Number(text);
        return Number.isFinite(value) ? value : null;
    }, [draft]);

    const validationError = useMemo(() => {
        if (draft.trim() === '') return null;
        if (amount === null || amount <= 0) {
            return 'Enter a positive amount.';
        }
        if (amount < MIN_ADD_USD) {
            return `Add at least $${MIN_ADD_USD.toFixed(2)}.`;
        }
        if (amount > MAX_ADD_USD) {
            return 'Add at most $1,000,000.00.';
        }
        if (Number(amount.toFixed(6)) !== amount) {
            return 'Use up to 6 decimal places.';
        }
        if (available !== null && amount > available) {
            return `Only ${fmtUsd(available)} is available to add.`;
        }
        return null;
    }, [draft, amount, available]);

    // Demo positions and the no-open-trade case have nothing to edit. Placed
    // after the hooks so the hook order stays stable across that transition.
    if (!enabled || !coin) return null;

    const canSubmit =
        !isPending &&
        isIsolated &&
        !!trade?.id &&
        typeof trade.margin === 'string' &&
        /^[1-9]\d{0,19}$/.test(trade.margin) &&
        amount !== null &&
        amount > 0 &&
        validationError === null;

    const applyChanges = () => {
        if (!canSubmit || amount === null || !trade?.margin) return;
        mutate(
            {
                coin,
                amountUsd: amount,
                tradeId: trade.id,
                expectedMargin: trade.margin,
            },
            { onSuccess: () => setExpanded(false) },
        );
    };

    const cancelEdit = () => {
        setExpanded(false);
    };

    const inputClass =
        'w-full rounded-md border border-border bg-background/40 px-2 py-1.5 font-mono text-[11px] tabular-nums transition-colors focus:outline-none focus:ring-2 focus:ring-ring/40 disabled:opacity-50';
    const labelClass =
        'text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground';

    return (
        <div className={cn('flex flex-col', compact ? 'gap-1.5' : 'gap-2')}>
            <button
                type="button"
                onClick={() => setExpanded((prev) => !prev)}
                aria-expanded={expanded}
                disabled={isPending}
                className="flex w-full cursor-pointer items-center gap-2 rounded-lg border border-border/60 bg-secondary/30 px-2.5 py-2 text-left transition-colors hover:bg-secondary/60"
            >
                <Plus size={11} className="shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate font-mono text-[10px] tabular-nums text-muted-foreground">
                    Margin{' '}
                    <span className="font-bold text-foreground">
                        {fmtUsd(position.marginUsed)}
                    </span>
                    {' · '}Available{' '}
                    <span className="font-bold text-foreground">
                        {fmtUsd(available)}
                    </span>
                </span>
                <span className="shrink-0 text-[9px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    Add margin
                </span>
                <ChevronDown
                    size={12}
                    className={cn(
                        'shrink-0 text-muted-foreground transition-transform duration-200',
                        expanded && 'rotate-180',
                    )}
                />
            </button>

            {expanded && (
                <div className="flex flex-col gap-2 rounded-xl border border-border bg-secondary/30 p-3">
                    {!isIsolated ? (
                        <p className="text-[10px] text-muted-foreground">
                            Margin can only be added to an isolated position.
                        </p>
                    ) : (
                        <>
                            <div className="flex flex-col gap-1">
                                <label
                                    htmlFor={`${fieldId}-amount`}
                                    className={labelClass}
                                >
                                    Amount to add (USDC)
                                </label>
                                <div className="flex items-center gap-2">
                                    <input
                                        id={`${fieldId}-amount`}
                                        type="number"
                                        inputMode="decimal"
                                        step="any"
                                        min="0"
                                        placeholder="0.00"
                                        disabled={isPending}
                                        value={draft}
                                        onChange={(e) =>
                                            setDraft(e.target.value)
                                        }
                                        className={inputClass}
                                    />
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        disabled={
                                            isPending ||
                                            available === null ||
                                            available < MIN_ADD_USD
                                        }
                                        onClick={() => setDraft(maxAmount)}
                                        className="shrink-0 px-2 text-[10px] font-semibold uppercase tracking-[0.08em]"
                                    >
                                        Max
                                    </Button>
                                </div>
                            </div>

                            <p className="text-[10px] text-muted-foreground">
                                Adding margin lowers the chance of liquidation
                                by moving the liquidation price away from the
                                mark. It comes out of your available trading
                                balance.
                            </p>

                            {validationError !== null && (
                                <p className="text-[10px] text-destructive">
                                    {validationError}
                                </p>
                            )}

                            <div className="flex items-center gap-2">
                                <Button
                                    size="sm"
                                    onClick={applyChanges}
                                    disabled={!canSubmit}
                                    className="flex-1"
                                >
                                    {isPending ? (
                                        <>
                                            <Loader2
                                                size={14}
                                                className="animate-spin"
                                            />
                                            <span>Adding…</span>
                                        </>
                                    ) : (
                                        'Add margin'
                                    )}
                                </Button>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={cancelEdit}
                                    disabled={isPending}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default PositionMarginEditor;
